// @flow

import React, {useState, useEffect, useRef} from "react";
import {Col, Table, Input, Switch, Button} from "antd";
import {useFormik} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {SubCompanyContainer, FilterContainer, SelectField} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {useCodPaymentBrandLabel} from "../../../hooks/useCodPaymentBrandLabel";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const CODPaymentOptions = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation();
    const codPaymentBrandLabel = useCodPaymentBrandLabel();

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: null,
                      city,
                      searchText,
                  })
                : () => [],
        dependencies: [
            companyData,
            currentPage,
            subCompanyRecall,
            city,
            searchText
        ]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                codPayment: {
                    hasTerminalRight: companyData?.data?.options?.codPayment?.hasTerminalRight,
                    allowedPaymentMethods: {
                        creditCard:
                            companyData?.data?.options?.codPayment?.allowedPaymentMethods
                                ?.creditCard,
                        mealCard:
                            companyData?.data?.options?.codPayment?.allowedPaymentMethods?.mealCard,
                        cash: companyData?.data?.options?.codPayment?.allowedPaymentMethods?.cash
                    }
                }
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);

            let filters = {};
            filters.searchText = searchText;
            filters.city = city;

            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            if (values.isActive) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const onSubCompanySubmit = async index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.codPayment;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;
        const params = {
            subCompanyId: submitData._id,
            hasTerminalRight: Boolean(submitData.hasTerminalRight),
            allowedPaymentMethods: {
                creditCard: Boolean(submitData.allowedPaymentMethods?.creditCard),
                mealCard: Boolean(submitData.allowedPaymentMethods?.mealCard),
                cash: Boolean(submitData.allowedPaymentMethods?.cash)
            }
        };
        const result = await stores.companyStore.updateSubCompanyCodPaymentSettings(params);
        /* if (result) {
            setSubCompanyRecall(!subCompanyRecallRef.current);
        } */
        showSubmitButton[subCompanyData.rows[index]?._id] = false;
        setIsSubCompanySubmitting(false);
    };

    return (
        <div data-testid="codPaymentOptionsScreen">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <Col span={12}>
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col span={6}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCurrentPage(1);
                                setCity(value);
                            }}
                        />
                    </Col>
                    <Col span={6}>
                        <h5>{t("filters.search", {ns: "common"})}</h5>
                        <Input.Search
                            data-testid="searchInput"
                            value={text}
                            onSearch={value => {
                                setCurrentPage(1);
                                setSearchText(value);
                            }}
                            onChange={event => {
                                setText(event.target.value);
                            }}
                        />
                    </Col>
                </FilterContainer>
            </Col>
            <SubCompanyContainer>
                <Table
                    rowKey="_id"
                    data-testid="subCompaniesTable"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push(
                                "/companies/cod-payment-options/" + companyId + "?page=" + page
                            );
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-textid="name">{text}</span>}
                    />
                    <Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-textid="city">{text}</span>}
                    />
                    <Column
                        dataIndex="hasTerminalRight"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={codPaymentBrandLabel}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="vigoposSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.codPayment
                                            ?.hasTerminalRight
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {
                                                ...newData.rows[index],
                                                options: {
                                                    ...newData.rows[index].options,
                                                    codPayment: {
                                                        ...newData.rows[index].options.codPayment,
                                                        hasTerminalRight: e
                                                    }
                                                }
                                            };
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="creditCard"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title="Kredi Kartı"
                        render={(text, record, index) => {
                            return (
                                <>
                                    <Switch
                                        data-testid="creditCardSwitch"
                                        checked={
                                            subCompanyData.rows[index]?.options?.codPayment
                                                ?.allowedPaymentMethods?.creditCard
                                        }
                                        onChange={e => {
                                            showSubmitButton[record._id] = true;
                                            setSubCompanyData(prevData => {
                                                const newData = {...prevData};
                                                const newItem = {
                                                    ...newData.rows[index],
                                                    options: {
                                                        ...newData.rows[index].options,
                                                        codPayment: {
                                                            ...newData.rows[index].options
                                                                .codPayment,
                                                            allowedPaymentMethods: {
                                                                ...newData.rows[index]?.options
                                                                    ?.codPayment
                                                                    ?.allowedPaymentMethods,
                                                                creditCard: e
                                                            }
                                                        }
                                                    }
                                                };
                                                newData.rows[index] = newItem;
                                                return newData;
                                            });
                                        }}
                                    />
                                </>
                            );
                        }}
                    />
                    <Column
                        dataIndex="mealCard"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title="Yemek Kartı"
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="mealCardSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.codPayment
                                            ?.allowedPaymentMethods?.mealCard
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {
                                                ...newData.rows[index],
                                                options: {
                                                    ...newData.rows[index].options,
                                                    codPayment: {
                                                        ...newData.rows[index].options.codPayment,
                                                        allowedPaymentMethods: {
                                                            ...newData.rows[index]?.options
                                                                ?.codPayment?.allowedPaymentMethods,
                                                            mealCard: e
                                                        }
                                                    }
                                                }
                                            };
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="cash"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title="Nakit"
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="cashSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.codPayment
                                            ?.allowedPaymentMethods?.cash
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {
                                                ...newData.rows[index],
                                                options: {
                                                    ...newData.rows[index].options,
                                                    codPayment: {
                                                        ...newData.rows[index].options.codPayment,
                                                        allowedPaymentMethods: {
                                                            ...newData.rows[index]?.options
                                                                ?.codPayment?.allowedPaymentMethods,
                                                            cash: e
                                                        }
                                                    }
                                                }
                                            };
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="_id"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        render={(text, record, index) =>
                            showSubmitButton[text] && (
                                <Button
                                    disabled={isSubCompanySubmitting}
                                    onClick={() => onSubCompanySubmit(index)}
                                    type="primary"
                                    data-testid="submitButton"
                                >
                                    {t("actions.save", {ns: "common"})}
                                </Button>
                            )
                        }
                    />
                </Table>
            </SubCompanyContainer>
        </div>
    );
};
